import { useQueryClient } from '@tanstack/react-query'
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, type RowSelectionState, useReactTable } from '@tanstack/react-table'
import { ArrowRight, Loader2, UserPlus, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { getBaseEmployersQuery, getBasePaySchedulesQuery } from 'components/hooks/payroll/utils'
import { useGetEmployees } from 'components/hooks/useGetEmployees'
import { useUpdatePayScheduleEmployees } from 'components/hooks/useUpdatePayScheduleEmployees'
import { TableSearchSingleColumn } from 'components/reusable/table/TableSearchSingleColumn'
import TanstackTable, { type PaginationMetadata } from 'components/reusable/table/TanstackTable'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'components/ui/sheet'
import { Button, CollapsibleContainer, useBreakpoint } from 'slab'
import { type PaySchedule } from 'types/payroll'

import { type EmployeeWithPaySchedules } from './types'

interface AddEmployeesToScheduleSheetProps {
  paySchedule: PaySchedule
  open: boolean
  onOpenChange: (open: boolean) => void
}

const columnHelper = createColumnHelper<EmployeeWithPaySchedules>()

const columns = [
  columnHelper.display({
    id: 'select',
    size: 30,
    header: ({ table }) => (
      <div className='w-7'>
        <input
          type='checkbox'
          checked={table.getIsAllRowsSelected()}
          onClick={(e) => {
            table.getToggleAllRowsSelectedHandler()(e)
          }}
          readOnly
          className='mx-auto h-4 w-4 rounded border-gray-300 text-thatch-purple focus:ring-thatch-purple'
        />
      </div>
    ),
    cell: ({ row }) => (
      <div className='w-7'>
        <input
          type='checkbox'
          checked={row.getIsSelected()}
          onClick={(e) => {
            row.getToggleSelectedHandler()(e)
          }}
          readOnly
          className='mx-auto h-4 w-4 rounded border-gray-300 text-thatch-purple focus:ring-thatch-purple'
        />
      </div>
    ),
  }),
  columnHelper.accessor((row) => `${row.first_name} ${row.last_name}`, {
    id: 'name',
    header: 'Name',
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('assigned_pay_schedules', {
    id: 'current_schedule',
    header: 'Current Pay Schedule(s)',
    cell: (info) => {
      const currentSchedules = info.getValue()?.filter(eps => !eps.end_date)
      return currentSchedules?.map(eps => eps.pay_schedule?.name).join(', ') || 'N/A'
    },
  }),
]

export function AddEmployeesToScheduleSheet ({ paySchedule, open, onOpenChange }: AddEmployeesToScheduleSheetProps) {
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: 10,
  })
  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata | undefined>()
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({})
  const [seenEmployees, setSeenEmployees] = useState<EmployeeWithPaySchedules[]>([])

  const { data: employees, isLoading: loadingEmployees } = useGetEmployees({ pagination, search: searchQuery })
  const { mutate: updatePayScheduleEmployees, isPending: isUpdatingPayScheduleEmployees } = useUpdatePayScheduleEmployees()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (employees) {
      setPaginationMetadata(employees.pagination)
    }
  }, [employees?.pagination])

  useEffect(() => {
    setSeenEmployees(prev => {
      const newSeenEmployees = employees?.data.filter((employee) => selectedRows[employee.id]) || []
      return [...prev, ...newSeenEmployees]
    })
  }, [selectedRows])

  const selectedEmployeeNames = Object.keys(selectedRows).map(id => {
    const employee = seenEmployees.find(employee => employee.id === id)
    return `${employee?.first_name} ${employee?.last_name}`
  })

  const handleSubmit = () => {
    updatePayScheduleEmployees({
      employeeIds: Object.keys(selectedRows),
      payScheduleId: paySchedule.id,
    }, {
      onSuccess: () => {
        toast.success('Employees added to pay schedule')
        queryClient.invalidateQueries({ queryKey: getBaseEmployersQuery() })
        queryClient.invalidateQueries({ queryKey: getBasePaySchedulesQuery() })
        onOpenChange(false)
      },
      onError: () => {
        toast.error('Failed to add employees to pay schedule')
      },
    })
  }

  const table = useReactTable({
    columns,
    data: employees?.data || [],
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: true,
    enableMultiRowSelection: true,
    enableColumnResizing: false,
    pageCount: employees?.pagination.total_pages,
    onPaginationChange: setPagination,
    getRowId: (row) => row.id,
    onRowSelectionChange: (updater) => {
      setSelectedRows((prev) => {
        const newSelection =
          typeof updater === 'function' ? updater(prev) : updater
        return newSelection
      })
    },
    manualPagination: true,
    state: {
      pagination,
      rowSelection: selectedRows
    },
  })

  const { breakpointIsAtLeast } = useBreakpoint()

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent responsiveLayout style={breakpointIsAtLeast('xs') ? { minWidth: '500px' } : {}} aria-describedby={undefined}>
        <SheetHeader>
          <SheetTitle className='heading-lg py-10'>Add employees to {paySchedule.name}</SheetTitle>
        </SheetHeader>

        <CollapsibleContainer
          defaultOpen={false}
          heading={
            <div className='flex items-center gap-2'>
              <UserPlus size={16} />
              <span>{Object.values(selectedRows).length} employees selected</span>
            </div>
          }
        >
          <div className='p-4 space-y-2 max-h-[196px] overflow-y-scroll scrollbar'>
            {selectedEmployeeNames.map((name, index) => (
              <div key={index} className='text-sm text-gray-800'>
                {name}
              </div>
            ))}
          </div>
        </CollapsibleContainer>

        <div className='text-sm text-gray-500 mt-2'>
          Employees added to the {paySchedule.name} will be removed from their current schedule.
        </div>

        <hr className='my-6' />

        <div className='space-y-6'>
          <div className='flex flex-col xs:flex-row justify-between gap-x-4'>
            <div className='flex-1'>
              <TableSearchSingleColumn
                table={table}
                columnId='name'
                placeholder='Find an employee'
                inputClassName='border-none w-full'
                containerClassName='shadow-none'
                onChange={(value) => {
                  setSearchQuery(value)
                  setPagination(prev => ({
                    ...prev,
                    pageIndex: 1,
                  }))
                }}
              />
            </div>
            <div className='flex-shrink-0 self-end'>
              <Button
                variant='tertiary'
                size='large'
                onClick={() => {
                  table.toggleAllRowsSelected(false)
                  setSelectedRows({})
                }}
              >
                De-select all
              </Button>
            </div>
          </div>

          <TanstackTable
            table={table}
            hideTableOutlines
            isLoading={loadingEmployees}
            isError={false}
            headerRowClasses='border-none bg-white text-[#737373] text-xs'
            noDataMessage='No employees found'
            paginationLabelSingular='employee'
            paginationLabelPlural='employees'
            tableClasses='table-fixed overflow-x-auto'
            rowClasses='max-w-[250px] break-words overflow-wrap-anywhere !whitespace-normal'
            paginationMetadata={paginationMetadata}
            onNextPageClick={() =>
              setPagination({
                ...pagination,
                pageIndex: paginationMetadata?.next_page || 1,
              })}
            onPreviousPageClick={() =>
              setPagination({
                ...pagination,
                pageIndex: paginationMetadata?.prev_page || 1,
              })}
          />

          <hr className='my-4' />
          <div className='flex flex-wrap justify-end gap-2'>
            <Button
              variant='secondary'
              leftIcon={X}
              size='large'
              onClick={() => {
                table.toggleAllRowsSelected(false)
                onOpenChange(false)
              }}
            >
              Discard Changes
            </Button>
            <Button
              variant='primary'
              rightIcon={ArrowRight}
              leftIcon={isUpdatingPayScheduleEmployees ? Loader2 : undefined}
              size='large'
              onClick={handleSubmit}
              disabled={isUpdatingPayScheduleEmployees}
            >
              Confirm Changes
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
